import { Paper, Box, Typography, Divider, Chip } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BedIcon from '@mui/icons-material/Bed';
import BathtubIcon from '@mui/icons-material/Bathtub';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import BuildIcon from '@mui/icons-material/Build';
import { HomeDetails } from '../types/business';
import { useHomeRepairsContext } from '../contexts/HomeRepairsContext';

interface HomeInfoProps {
  name: string;
  details: string;
  error?: string | null;
  compact?: boolean;
}

const HomeInfo = ({ name, details, error, compact = false }: HomeInfoProps) => {
  // Parse details as JSON, fallback to empty object if invalid
  let parsedDetails: HomeDetails = {};
  try {
    if (typeof details === 'string') {
      parsedDetails = JSON.parse(details);
    }
  } catch (e) {
    // If not valid JSON, use details as address for backward compatibility
    parsedDetails = { address: details };
  }
  
  // Extract values from parsed details with defaults
  const address = parsedDetails.address || '';
  const bedrooms = parsedDetails.bedrooms || 3;
  const bathrooms = parsedDetails.bathrooms || 2;
  const sqft = parsedDetails.sqft || 1800;
  const price = parsedDetails.price || 350000;

  // Get repair estimates from context - now with centralized calculation
  const { getRepairsSummary } = useHomeRepairsContext();
  const { unresolvedCount, averageRepairCost } = getRepairsSummary();

  return (
    <Paper 
      sx={{ 
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        mb: 3,
        p: compact ? 2 : 3
      }}
      elevation={2}
    >
      {/* Title and price row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
        <Typography 
          variant={compact ? "h6" : "h5"} 
          component="h2"
          sx={{ 
            fontWeight: 'bold', 
            mr: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {name || 'Home'}
        </Typography>
        <Typography 
          variant="h6" 
          color="primary" 
          sx={{ 
            fontWeight: 'bold',
            whiteSpace: 'nowrap'
          }}
        >
          {price ? `$${price.toLocaleString()}` : 'Price not available'}
        </Typography>
      </Box>
      
      {/* Address with icon */}
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
        <LocationOnIcon 
          sx={{ 
            fontSize: 16, 
            color: 'text.secondary',
            mr: 0.5
          }} 
        />
        {address ? (
          <Typography 
            variant="body2" 
            color="text.secondary"
            component="a"
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ 
              textAlign: 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textDecoration: 'none',
              color: 'primary.main',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            {address}
          </Typography>
        ) : (
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              textAlign: 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            Address not available
          </Typography>
        )}
      </Box>
      
      <Divider sx={{ my: 1.5 }} />
      
      {/* Property features as tags with icons */}
      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Chip
          icon={<BedIcon />}
          label={`${bedrooms} Bed`}
          variant="outlined"
          size={compact ? "small" : "medium"}
          sx={{ 
            borderRadius: '16px',
            '& .MuiChip-label': {
              px: 1.5,
              py: 0.5,
            },
            '& .MuiChip-icon': {
              ml: 1,
            }
          }}
        />
        <Chip
          icon={<BathtubIcon  />}
          label={`${bathrooms} Bath`}
          variant="outlined"
          size={compact ? "small" : "medium"}
          sx={{ 
            borderRadius: '16px',
            '& .MuiChip-label': {
              px: 1.5,
              py: 0.5,
              mx: 0,
            },
            '& .MuiChip-icon': {
              ml: 1,
            }
          }}
        />
        <Chip
          icon={<SquareFootIcon />}
          label={`${sqft} sqft`}
          variant="outlined"
          size={compact ? "small" : "medium"}
          sx={{ 
            borderRadius: '16px',
            '& .MuiChip-label': {
              px: 1.5,
              py: 0.5,
            },
            '& .MuiChip-icon': {
              ml: 1,
            }
          }}
        />
        
        {/* Repair estimate chip - now using centralized calculation */}
        {unresolvedCount > 0 && (
          <Chip
            icon={<BuildIcon />}
            label={`~$${averageRepairCost.toLocaleString()} repairs`}
            variant="outlined"
            size={compact ? "small" : "medium"}
            color="warning"
            sx={{ 
              borderRadius: '16px',
              '& .MuiChip-label': {
                px: 1.5,
                py: 0.5,
              },
              '& .MuiChip-icon': {
                ml: 1,
              }
            }}
          />
        )}
      </Box>
      
      {error && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Box>
      )}
      
      {!compact && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {error ? 
              `Error: ${error}` : 
              'This property features modern amenities, an open floor plan, and has been recently updated with new appliances. The backyard is fully fenced and includes a covered patio area perfect for entertaining.'}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default HomeInfo;